import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Stamp, Landmark, MapPin, Calendar, ChevronRight, Users, Star, Check, Compass, Clock } from "lucide-react";

const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

const TIER_META = {
  primary: { label: "Your primary county", tone: "border-amber-200 bg-amber-50 text-amber-700", Icon: Star },
  also: { label: "Your also-willing county", tone: "border-indigo-200 bg-indigo-50 text-indigo-700", Icon: Check },
  no_preference: { label: "No county preference set", tone: "border-slate-200 bg-slate-50 text-slate-500", Icon: Compass },
  out_of_area: { label: "Outside your counties", tone: "border-slate-200 bg-slate-50 text-slate-500", Icon: MapPin },
};

function OpportunityBadge({ tier }) {
  const m = TIER_META[tier] || TIER_META.out_of_area;
  const Icon = m.Icon;
  return (
    <span data-testid={`notary-opp-tier-${tier}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.tone}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

export default function NotaryAssignments() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [opps, setOpps] = useState(null);
  const [claiming, setClaiming] = useState(null);

  const loadOpps = () => api.get("/server/notary-opportunities").then(({ data }) => setOpps(data)).catch(() => setOpps({ items: [] }));

  useEffect(() => {
    if (user && !["notary", "both"].includes(user.service_capability)) { nav("/server", { replace: true }); return; }
    api.get("/notary/portal/assignments").then(({ data }) => setRows(data)).catch(() => setRows([]));
    loadOpps();
  }, [user, nav]);

  const claim = async (id) => {
    setClaiming(id);
    try {
      await api.post(`/orders/${id}/claim`);
      toast.success("Job claimed — it's now yours");
      loadOpps();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not claim this job");
    } finally {
      setClaiming(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="notary-portal-list">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Notary Assignments</h2>
        <p className="text-slate-500">Assignments routed to you. Open a job to verify signers, capture evidence and complete the notarization.</p>
      </div>

      {/* Available notary opportunities — tier-sorted for your counties */}
      <div data-testid="notary-opportunities">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-slate-900">Available notary opportunities</h3>
          {opps && <span className="text-xs text-slate-400" data-testid="notary-opp-count">{opps.total} open</span>}
        </div>
        {opps === null && <p className="text-sm text-slate-400">Loading opportunities…</p>}
        {opps && opps.items.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="notary-opp-empty">
            No open notary opportunities right now.
          </div>
        )}
        <div className="space-y-2.5">
          {(opps?.items || []).map((o) => (
            <div key={o.id} data-testid={`notary-opp-card-${o.id}`} className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-900">{o.job_number || "Notary job"}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{o.service_type || "Notary"}</span>
                  <OpportunityBadge tier={o.tier} />
                </div>
                <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-slate-400">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {o.service_area || o.county || "—"}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {o.deadline ? fmt(o.deadline) : "No deadline"}</span>
                </div>
              </div>
              <Button data-testid={`notary-opp-claim-${o.id}`} size="sm" disabled={claiming === o.id} onClick={() => claim(o.id)} className="bg-indigo-600 text-white hover:bg-indigo-700">
                {claiming === o.id ? "Claiming…" : "Claim job"}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Assignments routed to you */}
      <div>
        <h3 className="mb-2 font-display text-lg font-bold text-slate-900">Your assignments</h3>
        {rows === null && <p className="text-sm text-slate-400">Loading…</p>}
        {rows && rows.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500" data-testid="notary-portal-empty">
            No notary assignments yet. Claim an opportunity above, or an admin can assign one to you.
          </div>
        )}
        <div className="space-y-3">
          {(rows || []).map((r) => (
            <button
              key={r.id}
              data-testid={`notary-portal-card-${r.id}`}
              onClick={() => nav(`/server/notary/${r.id}`)}
              className="group flex w-full items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-indigo-400 hover:shadow-sm"
            >
              <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${r.enhanced_evidence ? "bg-amber-100 text-amber-700" : "bg-indigo-100 text-indigo-700"}`}>
                {r.enhanced_evidence ? <Landmark className="h-6 w-6" /> : <Stamp className="h-6 w-6" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate font-semibold text-slate-900">{r.client_name}</p>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{r.status}</span>
                  {r.enhanced_evidence && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Enhanced</span>}
                </div>
                <p className="truncate text-sm text-slate-500">{r.service_type || "Notary"} · {r.document_category || "—"}</p>
                <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-400">
                  <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {r.num_signers} signer(s)</span>
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {r.county || r.service_address || "—"}</span>
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmt(r.appointment_datetime)}</span>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-indigo-500" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
